import React, { useState, useRef, useCallback, useEffect } from "react";
import { getAuthHeaders } from "../utils/auth";

const API = "/api";

export default function LiveTranslation({ onBack }) {
  const [listening, setListening]   = useState(false);
  const [segments, setSegments]     = useState([]);  // [{english, chinese, id}]
  const [error, setError]           = useState(null);
  const [status, setStatus]         = useState("idle"); // idle | listening | processing
  const [audioSource, setAudioSource] = useState("mic"); // "mic" | "system"

  const mediaRecRef    = useRef(null);
  const streamRef      = useRef(null);
  const displayStreamRef = useRef(null); // for getDisplayMedia video track cleanup
  const intervalRef    = useRef(null);
  const chunksRef      = useRef([]);
  const segIdRef       = useRef(0);
  const scrollRef      = useRef(null);

  // Auto-scroll to bottom when new segments arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [segments]);

  const sendChunk = useCallback(async (blob) => {
    if (blob.size < 1000) return; // skip near-empty chunks
    setStatus("processing");
    try {
      const authHeaders = await getAuthHeaders();
      const form = new FormData();
      form.append("audio", blob, "chunk.webm");
      const res = await fetch(`${API}/live-translate`, {
        method: "POST",
        headers: { ...authHeaders },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Translation failed");
      }
      const data = await res.json();
      if (data.english && data.english.trim()) {
        setSegments((prev) => [
          ...prev,
          { english: data.english.trim(), chinese: data.chinese.trim(), id: ++segIdRef.current },
        ]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setStatus((s) => (s === "processing" ? "listening" : s));
    }
  }, []);

  const startListening = useCallback(async () => {
    setError(null);
    try {
      let audioStream;

      if (audioSource === "system") {
        // Use getDisplayMedia to capture system/tab audio
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: 1, height: 1 },  // minimal video (required by API, we don't use it)
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
          systemAudio: "include",           // pre-check "Also share system audio"
          monitorTypeSurfaces: "include",   // ensure Entire screen option is available
          surfaceSwitching: "exclude",      // hide the "switch tab" button
          selfBrowserSurface: "exclude",    // hide current tab to avoid feedback loop
        });
        const audioTracks = displayStream.getAudioTracks();
        if (audioTracks.length === 0) {
          displayStream.getTracks().forEach((t) => t.stop());
          setError("No audio track found. Make sure to check \"Share audio\" when sharing.");
          return;
        }
        // Keep only the audio track for recording
        audioStream = new MediaStream(audioTracks);
        // Store the display stream so we can stop video tracks on cleanup
        displayStreamRef.current = displayStream;
        // If the user stops sharing via the browser UI, auto-stop
        displayStream.getVideoTracks().forEach((t) => {
          t.onended = () => stopListening();
        });
      } else {
        audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      streamRef.current = audioStream;

      const startRecorder = () => {
        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";
        const rec = new MediaRecorder(audioStream, { mimeType });
        chunksRef.current = [];

        rec.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        rec.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType });
          sendChunk(blob);
        };

        rec.start();
        mediaRecRef.current = rec;
      };

      startRecorder();

      // Every 4 seconds, stop the recorder (triggers onstop → sendChunk), then start a new one
      intervalRef.current = setInterval(() => {
        if (mediaRecRef.current && mediaRecRef.current.state === "recording") {
          mediaRecRef.current.stop();
        }
        // Small delay to let the onstop fire before starting new recorder
        setTimeout(() => {
          if (streamRef.current && streamRef.current.active) {
            startRecorder();
          }
        }, 100);
      }, 4000);

      setListening(true);
      setStatus("listening");
    } catch (e) {
      if (e.name === "NotAllowedError") {
        setError(audioSource === "system"
          ? "Screen sharing was cancelled. Please try again and share a tab/window."
          : "Microphone access denied. Please allow microphone permission.");
      } else {
        setError(e.message);
      }
    }
  }, [sendChunk, audioSource]);

  const stopListening = useCallback(() => {
    // Stop interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Stop current recorder
    if (mediaRecRef.current && mediaRecRef.current.state === "recording") {
      mediaRecRef.current.stop();
    }
    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    // Stop display stream (system audio mode)
    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach((t) => t.stop());
      displayStreamRef.current = null;
    }
    setListening(false);
    setStatus("idle");
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (mediaRecRef.current && mediaRecRef.current.state === "recording") {
        mediaRecRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (displayStreamRef.current) {
        displayStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  return (
    <div className="lt-page">
      {/* Header bar */}
      <header className="lt-header">
        <button className="lt-back-btn" onClick={onBack}>← Back</button>
        <div className="lt-header-title">
          <span className="lt-title">Live Translation</span>
        </div>
        <div className="lt-header-status">
          {status === "listening" && <span className="lt-status-dot lt-dot-green" />}
          {status === "processing" && <span className="lt-status-dot lt-dot-amber" />}
          {status === "idle" && <span className="lt-status-dot lt-dot-gray" />}
          <span className="lt-status-label">
            {status === "listening" ? "Listening…" : status === "processing" ? "Processing…" : "Ready"}
          </span>
        </div>
      </header>

      {/* Tip banner */}
      {listening && (
        <div style={{
          margin: "10px 20px 0",
          fontSize: 12,
          color: "#475569",
          textAlign: "center",
        }}>
          💡 当没有声音时，AI 可能会生成无关内容，请忽略。
        </div>
      )}

      {/* Main transcript area */}
      <div className="lt-body" ref={scrollRef}>
        {segments.length === 0 && !listening && (
          <div className="lt-empty">
            <div className="lt-empty-icon">🎙️</div>
            <h2>实时翻译</h2>
            <p>选择音频来源，然后点击下方按钮开始。<br/>
            英语语音将被实时转录并翻译为中文。</p>
          </div>
        )}
        {segments.length === 0 && listening && (
          <div className="lt-empty">
            <div className="lt-pulse-ring" />
            <p className="lt-listening-hint">正在聆听… 请开始说话或播放音频。</p>
          </div>
        )}
        {segments.map((seg) => (
          <div className="lt-segment" key={seg.id}>
            <div className="lt-seg-en">
              <span className="lt-seg-lang">EN</span>
              {seg.english}
            </div>
            <div className="lt-seg-zh">
              <span className="lt-seg-lang lt-seg-lang-zh">中文</span>
              {seg.chinese}
            </div>
          </div>
        ))}
      </div>

      {/* Error banner */}
      {error && (
        <div className="lt-error">
          ⚠️ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Audio source toggle */}
      {!listening && (
        <div className="lt-source-toggle">
          <button
            className={`lt-source-btn ${audioSource === "mic" ? "lt-source-active" : ""}`}
            onClick={() => setAudioSource("mic")}
          >
            🎙️ 麦克风
            <span style={{ fontSize: 11, opacity: 0.7, display: "block", marginTop: 2 }}>适合线下上课</span>
          </button>
          <button
            className={`lt-source-btn ${audioSource === "system" ? "lt-source-active" : ""}`}
            onClick={() => setAudioSource("system")}
          >
            🔊 电脑声音
            <span style={{ fontSize: 11, opacity: 0.7, display: "block", marginTop: 2 }}>适合网课和录播</span>
          </button>
        </div>
      )}
      {!listening && (
        <div className="lt-audio-hint">
          {audioSource === "mic"
            ? "💡 使用电脑麦克风收音，适合线下上课"
            : "💡 捕获电脑内部播放的声音，点击后需要选择一个窗口并确保底部的 \"Also share system audio\" 已打开。"}
        </div>
      )}

      {/* Controls */}
      <div className="lt-controls">
        {!listening ? (
          <button className="lt-mic-btn lt-mic-start" onClick={startListening}>
            <span className="lt-mic-icon">{audioSource === "mic" ? "🎙️" : "🔊"}</span>
            {audioSource === "mic" ? "开始听写" : "选择窗口并开始"}
          </button>
        ) : (
          <button className="lt-mic-btn lt-mic-stop" onClick={stopListening}>
            <span className="lt-mic-icon">⏹️</span>
            Stop
          </button>
        )}
        {segments.length > 0 && (
          <button className="lt-clear-btn" onClick={() => setSegments([])}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
